import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Alert, Loading, Text } from '@goodfynd/react-web.lib.ui';
import TextField from '@mui/material/TextField';
import { observer } from 'mobx-react-lite';
import { useRouter } from 'next/router';

import config from '../../config';
import images from '../../config/images';
import strings from '../../config/strings';
import { useApp } from '../../context/app-context';
import { useCartDispatch } from '../../context/cart-context';
import { useProductApi } from '../../hooks/api';
import { useOrderActions } from '../../hooks/useOrderActions';
import type { ProductResponse } from '../../services/api/types';
import { useStores } from '../../stores';
import * as numberUtil from '../../utils/number-util';
import * as storageUtil from '../../utils/storage-util';
import InputQuantity from '../forms/InputQuantity/InputQuantity';
import Modal from '../Modal';
import PickupTimeOptions from '../PickupTimeOptions';

import ProductOptionsGroup from './ProductOptionsGroup';
import {
  StyledAddButton,
  StyledImageContainer,
  StyledLikeButton,
  StyledModalContent,
  StyledRequestsContainer,
  StyledShareButton,
  StyledStickyBottom,
} from './styles';
import type { ProductModalProps } from './types';

import classes from './ProductModal.module.css';

const ProductModal = (props: ProductModalProps) => {
  const { setCart } = useCartDispatch();

  const [errors, setErrors] = useState<string[]>([]);
  const [requests, setRequests] = useState('');
  const [viewPickupTimeOptions, togglePickupTimeOptions] = useState(false);

  const refs = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
  const [invalidGroup, setInvalidGroup] = useState('');

  const {
    user: { isGuest },
  } = useApp();

  const {
    orderStore: { busy, event, request, startOrder },
  } = useStores();

  const { addItem } = useOrderActions();

  const [quantity, setQuantity] = useState(1);
  const {
    pathname,
    query: { params, id: scheduleId },
  } = useRouter();
  const id = params?.[params.length - 1] as string;

  /* Fetch product data */
  const api = useProductApi();
  const { data, isLoading } = useQuery<ProductResponse, unknown>(
    id,
    () => {
      return api.get(id);
    },
    {
      refetchOnMount: true,
    }
  );

  useEffect(() => {
    if (!event?.pickupTime) {
      togglePickupTimeOptions(true);
    }
  }, [event?.pickupTime]);

  useEffect(() => {
    if (data && data?.product?.id === id && event?.pickupTime) {
      const product = data.product;
      startOrder(id, {
        id: '',
        isDonation: false,
        optionGroups: product.optionGroups.map((group) => ({
          ...group,
          limit: group.limit || 0,
          options: [],
        })),
        orderId: '',
        pickupTime: undefined,
        productId: product.id,
        quantity: quantity,
        requests,
        scheduleId: '',
        sector: 'trucks',
        value: product.price.value || 0, // price
      });
    }
  }, [data, event?.pickupTime, id, quantity, requests, startOrder]);

  if (!isLoading && !data?.product) {
    return null;
  }

  const isValid = () => {
    if (!request?.optionGroups.length) {
      return true;
    }

    for (let i = 0; i < request.optionGroups.length; i++) {
      const group = request.optionGroups[i];
      const valid = !group.required || group.options.length > 0;
      if (valid) {
        continue;
      }

      const groupId = group.id;
      const ref = refs.current[groupId];
      ref.current?.scrollIntoView({ behavior: 'smooth' });
      setInvalidGroup(groupId);
      return false;
    }

    return true;
  };

  const handleAdd = async () => {
    if (data?.product && isValid()) {
      try {
        setErrors([]);
        const cart = await addItem(quantity, requests, data.product.name);
        setCart(cart);

        if (isGuest) {
          storageUtil.local.set(config.storageKeys.guestOrder, cart.list[0].id);
        } else {
          storageUtil.local.remove(config.storageKeys.guestOrder);
        }

        props.close();
      } catch (error: any) {
        console.warn(error);

        setErrors(
          error
            ? [
                error.message,
                ...(error.errors || [])
                  .filter((error: ResponseError) => error.code !== 'trace')
                  .map((error: ResponseError) => error.message),
              ]
            : []
        );
      }
    }
  };

  const renderContent = () => {
    if (busy || isLoading) {
      return (
        <div>
          <Loading type="line" />
        </div>
      );
    }

    if (!data?.product) {
      return <></>;
    }

    const product = data.product;
    return (
      <div>
        <StyledImageContainer
          css={{
            backgroundImage: `url(${
              product.image || images.fallbacks.product
            })`,
            backgroundPosition: 'center',
            backgroundSize: 'cover',
          }}
        >
          <StyledLikeButton
            buttonStyle="icon"
            count={product.likes}
            item={product}
            liked={product.liked}
            type="product"
          />

          <StyledShareButton
            buttonStyle="icon"
            item={product}
            text={`Check out ${product.name} from ${product.vendor.uniqueName} on Goodfynd!`}
            title={`${product.name} @ ${product.vendor.uniqueName}`}
            url={product.shortUrl}
          />
        </StyledImageContainer>

        <StyledModalContent
          css={{
            paddingBottom: isDisabled ? 24 : undefined,
          }}
        >
          <Text as="heading5">{product.name}</Text>
          <Text as="body2" style={{ marginTop: 8 }}>
            {product.description}
          </Text>

          {isDisabled && (
            <Alert className={classes['disabled-alert']} status="warning">
              {event?.pickupTime ? (
                strings.labels.itemNotAvailable
              ) : (
                <Text
                  color="TextIconFavorable"
                  onClick={() => togglePickupTimeOptions(true)}
                  style={{ cursor: 'pointer' }}
                >
                  {strings.titles.choosePickupOptions}
                </Text>
              )}
            </Alert>
          )}

          {errors.length > 0 && (
            <Alert status="error" style={{ marginTop: 12 }}>
              {errors}
            </Alert>
          )}

          <ProductOptionsGroup
            groups={product.optionGroups}
            invalidGroup={invalidGroup}
            refs={refs}
          />

          {product.allowSpecialRequests && (
            <StyledRequestsContainer>
              <TextField
                fullWidth
                helperText={strings.labels.leaveNotes}
                label={strings.labels.specialRequests}
                onChange={(e) => setRequests(e.target.value)}
                value={requests}
              />
            </StyledRequestsContainer>
          )}
        </StyledModalContent>
      </div>
    );
  };

  const product = data?.product;
  const isDisabled = !event?.pickupTime || !product?.price.value;
  return (
    <>
      <Modal
        aria-describedby="product-modal-ordering"
        aria-labelledby="product-modal"
        footer={
          isDisabled ? undefined : (
            <StyledStickyBottom>
              <InputQuantity
                defaultValue={quantity}
                disabled={isDisabled}
                max={5}
                min={1}
                onChange={(quantity) => setQuantity(quantity)}
              />

              <StyledAddButton disabled={isDisabled} onClick={handleAdd}>
                {product?.price.value
                  ? strings.labels.add +
                    config.unicode.centerDotPadded +
                    numberUtil.renderPrice(
                      (product?.price.value +
                        (request?.optionGroups
                          .map((group) =>
                            group.options
                              .map((option) => option.price || 0)
                              .reduce((a, b) => a + b, 0)
                          )
                          .reduce((a, b) => a + b, 0) || 0)) *
                        quantity
                    )
                  : strings.labels.notForSale}
              </StyledAddButton>
            </StyledStickyBottom>
          )
        }
        {...props}
      >
        {renderContent()}
      </Modal>

      {product && viewPickupTimeOptions && (
        <PickupTimeOptions
          onContinue={() => togglePickupTimeOptions(false)}
          open={viewPickupTimeOptions}
          vendorId={product.vendor.nameId}
          close={() => {
            togglePickupTimeOptions(false);
            // !event?.pickupTime && props.close();
          }}
          scheduleId={
            //TODO find a better way to  get the schedule id.
            pathname.includes('schedules') ? (scheduleId as string) : undefined
          }
        />
      )}
    </>
  );
};

export default observer(ProductModal);
